import React, { useState, useRef, useEffect } from "react";
import "date-fns";
import {
  Button,
  Fade,
  FormControl,
  InputLabel,
  Paper,
  Popper,
  MenuItem,
  Grid,
  Select,
  IconButton,
} from "@material-ui/core";

import { useStyles } from "./FilterDropDown.styles";
import { Add, Close } from "@material-ui/icons";
import { BadgeButton } from "../BadgeButton/BadgeButton";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { ValueField } from "./ValueField";

const FilterDropDown = ({
  clearFilters,
  applyFilters,
  selectedFilters,
  opened,
  timeFilter,
  setReportRange,
  reportRange,
  isLoading = false,
  ...props
}) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const prevOpen = useRef(open);
  const { t } = useTranslation(["common"]);
  const filterEmptyState = [{ column: "", value: "" }];
  const [row, setRow] = useState(filterEmptyState);
  const [initialValue, setInitialValue] = useState("");

  const handleToggle = () => {
    setOpen(!open);
    opened(open);
  };

  useEffect(() => {
    if (selectedFilters.length) {
      setRow(selectedFilters);
    }
  }, [selectedFilters]);

  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }

    prevOpen.current = open;
  }, [open]);

  const addRow = () => {
    setRow([...row, { column: "", value: "" }]);
  };

  const deleteRule = (indexForDelete) => {
    setRow([...row.filter((rule, ruleIndex) => ruleIndex !== indexForDelete)]);
  };

  const rowRender = (rule, index) => {
    const menuProps = {
      PaperProps: {
        style: {
          maxHeight: 200,
        },
      },
    };

    const availableFilters = props?.filterItems.filter((ad) => {
      if (ad.column === "serialNumber") {
        setInitialValue(ad);
      }
      return (
        row.every((fd) => fd?.column?.column !== ad?.column) ||
        rule?.column?.column === ad?.column
      );
    });

    return (
      <div className={classes.row} key={index}>
        <Grid
          container
          direction="row"
          justifyContent="center"
          alignItems="center"
        >
          <Grid item sm={6}>
            <FormControl className={classes.formControl}>
              <InputLabel shrink id={"filterColumnLabel" + index}>
                {t("common:filter.column")}
              </InputLabel>
              <Select
                labelId={"filterColumnLabel" + index}
                id={"filterColumnSelectPlaceholderLabel" + index}
                value={rule.column}
                defaultValue={() => {
                  const currentRows = row;
                  currentRows[index] = {
                    column: initialValue,
                    value: "",
                  };
                  setRow([...currentRows]);
                }}
                onChange={(e) => {
                  const currentRows = row;
                  currentRows[index] = {
                    column: e.target.value,
                    value: "",
                  };
                  setRow([...currentRows]);
                }}
                displayEmpty
                className={classes.selectEmpty}
              >
                <MenuItem value="" className={classes.placeholder}>
                  {t("common:filter.selectcol")}
                </MenuItem>
                {availableFilters.map((data, i) => {
                  return (
                    <MenuItem key={i} value={data} className={classes.menuList}>
                      {data.columnLabel}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
          <Grid item sm={6}>
            <FormControl className={classes.formControl}>
              <InputLabel shrink id={"filterValueLabel" + index}>
                {t("common:filter.value")}
              </InputLabel>
              <ValueField
                menuProps={menuProps}
                row={row}
                setRow={(val) => setRow(val)}
                index={index}
                rule={rule}
              />
            </FormControl>
          </Grid>
        </Grid>
        <IconButton
          aria-label="delete"
          className={`${classes.iconBtn} ${
            !(index !== 0 && row.length > 1) ? classes.hidden : ""
          }`}
          size="small"
          onClick={() => deleteRule(index)}
        >
          <Close fontSize="inherit" />
        </IconButton>
      </div>
    );
  };

  return (
    <>
      <Button
        className={`${classes.button} ${open && classes.opened}`}
        ref={anchorRef}
        aria-controls={open ? "menu-list-grow" : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
        data-testid="open-filters"
        disabled={isLoading}
      >
        {open && (
          <Close className={[classes.icon, classes.iconSize].join(" ")} />
        )}
        {props.icon && !open && (
          <div className={classes.icon}>{props.icon}</div>
        )}
        {props.label}
      </Button>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement={"bottom-start"}
        transition
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={350}>
            <Paper className={classes.paper}>
              <div className={classes.filterGrp}>
                {timeFilter &&
                  timeFilter.map((item) => {
                    return (
                      <>
                        <BadgeButton
                          type="primary"
                          variant={
                            reportRange.value === item.value
                              ? "contained"
                              : "outlined"
                          }
                          label={item.label}
                          modal={false}
                          clBck={() => setRow([])}
                          btnClick={() => {
                            handleToggle();
                            setReportRange(item);
                          }}
                        />
                      </>
                    );
                  })}
              </div>
              <div className={classes.content}>
                {row.map((rule, index) => rowRender(rule, index))}
              </div>
              <div className={classes.footer} data-testid="add-btn">
                <Button
                  color="primary"
                  className={classes.addBtn}
                  onClick={addRow}
                  disabled={row?.length === props?.filterItems?.length}
                >
                  <Add />
                  {t("common:filter.add")}
                </Button>
                <div className={classes.btnGrp} data-testid="btn-wrapper">
                  <BadgeButton
                    type="primary"
                    variant="outlined"
                    label={t("common:filter.reset")}
                    modal={false}
                    clBck={() => setRow([])}
                    btnClick={() => {
                      handleToggle();
                      clearFilters();
                    }}
                  />
                  <BadgeButton
                    type="primary"
                    label={t("common:filter.apply")}
                    modal={false}
                    testid="apply-filters"
                    btnClick={() => {
                      handleToggle();
                      applyFilters(row);
                    }}
                  />
                </div>
              </div>
            </Paper>
          </Fade>
        )}
      </Popper>
    </>
  );
};

FilterDropDown.propTypes = {
  clearFilters: PropTypes.func,
  applyFilters: PropTypes.func,
  selectedFilters: PropTypes.array,
  filterItems: PropTypes.array,
  icon: PropTypes.node,
  label: PropTypes.string,
  opened: PropTypes.func,
  timeFilter: PropTypes.array,
  setReportRange: PropTypes.func,
  reportRange: PropTypes.string,
  isLoading: PropTypes.bool,
};

export { FilterDropDown };
